// src/data/nameWords.js
// ─────────────────────────────────────────────────────────────────────────────
// Word pools for the in-game username builder.
// Names are English-only for global leaderboard consistency.
// Each pool has a `words` array and an `unlock` condition.
// ─────────────────────────────────────────────────────────────────────────────

export const NAME_SLOTS = [

  // ── Slot 0: Creature (required, default) ──────────────────────────────────
  {
    id: 'creature',
    required: true,
    unlock: null, // always available
    words: [
      'Pyron', 'Voltrix', 'Aquyra', 'Sylrana', 'Athena', 'Noxys',
    ],
  },

  // ── Slot 1: Verb (required, default) ──────────────────────────────────────
  {
    id: 'verb',
    required: true,
    unlock: null,
    words: [
      'loves', 'wears', 'eats', 'rides', 'fears',
      'hugs', 'throws', 'hides', 'cooks', 'collects',
      'chases', 'draws', 'kicks', 'finds', 'drops',
    ],
  },

  // ── Slot 2: Noun (required, default) ──────────────────────────────────────
  {
    id: 'noun',
    required: true,
    unlock: null,
    words: [
      'pizza', 'socks', 'dragons', 'tacos', 'clouds',
      'pants', 'robots', 'noodles', 'cats', 'thunder',
      'waffles', 'swords', 'pickles', 'donuts', 'ghosts',
      'muffins', 'comets', 'burritos', 'llamas', 'pancakes',
    ],
  },

  // ── Slot 3: Title — basic (optional, unlock: complete World 1) ────────────
  {
    id: 'title',
    required: false,
    unlock: { type: 'worlds_completed', value: 1 },
    words: [
      'Storm', 'Dark', 'Wild', 'Neon', 'Frost',
      'Iron', 'Blaze', 'Swift', 'Brave', 'Lucky',
    ],
  },

  // ── Slot 4: Role (optional, unlock: complete World 3) ─────────────────────
  {
    id: 'role',
    required: false,
    unlock: { type: 'worlds_completed', value: 3 },
    words: [
      'Tamer', 'Breaker', 'Seeker', 'Guardian', 'Warden',
      'Hunter', 'Crusher', 'Binder', 'Ranger', 'Sage',
    ],
  },

  // ── Slot 5: Place (optional, unlock: complete all 5 worlds) ───────────────
  {
    id: 'place',
    required: false,
    unlock: { type: 'worlds_completed', value: 5 },
    prefix: 'of',
    words: [
      'Ignarion', 'Hydrallis', 'Terravael', 'Voltaryn', 'Aetherion',
    ],
  },

  // ── Slot 6: Title — rare (optional, unlock: Infinite wave 10+) ────────────
  {
    id: 'title_rare',
    required: false,
    unlock: { type: 'infinite_wave', value: 10 },
    replaces: 'title', // upgrades the title slot
    words: [
      'Legendary', 'Mythic', 'Prismara\'s', 'Eternal', 'Supreme',
      'Celestial', 'Phantom', 'Omega', 'Apex', 'Ascended',
    ],
  },

  // ── Slot 7: Emoji (optional, default) ─────────────────────────────────────
  {
    id: 'emoji',
    required: false,
    unlock: null,
    words: [
      '🔥', '⚡', '💧', '🌿', '🔮',
      '⭐', '💎', '👑', '💀', '🎮',
      '🚀', '🏆', '❤️', '🐉', '🌋',
      '🌊', '🌳', '⛈️', '🧠', '✨',
    ],
  },
];

// ── Construct display name from selections ──────────────────────────────────
// selections = { creature: 'Pyron', verb: 'eats', noun: 'tacos', title: 'Storm', ... }
export function buildDisplayName(selections, year, flag) {
  const parts = [];

  // Title (rare overrides basic)
  if (selections.title_rare) parts.push(selections.title_rare);
  else if (selections.title) parts.push(selections.title);

  // Role
  if (selections.role) parts.push(selections.role);

  // Creature (required)
  parts.push(selections.creature);

  // Verb + Noun (required)
  parts.push(selections.verb);
  parts.push(selections.noun);

  // Place (with prefix)
  if (selections.place) parts.push(`of ${selections.place}`);

  // Year
  if (year) parts.push(String(year).slice(-2));

  // Flag
  if (flag) parts.push(flag);

  // Emoji
  if (selections.emoji) parts.push(selections.emoji);

  return parts.join(' ');
}

// ── Short name for leaderboard (without flag, it's shown separately) ────────
export function buildLeaderboardName(selections, year) {
  const parts = [];

  if (selections.title_rare) parts.push(selections.title_rare);
  else if (selections.title) parts.push(selections.title);

  if (selections.role) parts.push(selections.role);

  parts.push(selections.creature);
  parts.push(selections.verb);
  parts.push(selections.noun);

  if (selections.place) parts.push(`of ${selections.place}`);
  if (year) parts.push(String(year).slice(-2));
  if (selections.emoji) parts.push(selections.emoji);

  return parts.join(' ');
}
